import { z } from 'zod';

/**
 * Questionnaires on module records, e.g. the general information of an inspection
 * (docs/architecture/questionnaires.md, ADR-0039). Companies keep templates; a record stores a
 * snapshot of the questions with their answers, so it goes offline with the record and later
 * changes of the template leave it as it was.
 */

/** A questionnaire a module offers for records of an entity (manifest `questionnaires`). */
export interface QuestionnaireDeclaration {
  /** `<module>.<name>`, e.g. `fire-safety.general`. */
  key: string;
  entity: string;
  /** i18n key. */
  label: string;
  /** Key of a document of the module whose placeholders defaults may use, e.g. `{{object.name}}`. */
  document?: string;
}

export const QUESTION_TYPES = ['text', 'number', 'yes-no', 'date', 'choice'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export interface Question {
  /** Stable within the template, so answers survive reordering. */
  id: string;
  label: string;
  type: QuestionType;
  /** For `choice`. */
  options?: string[];
  /** Preset answer; texts may hold placeholders, resolved when the snapshot is taken. */
  default?: string | number | boolean | null;
  required?: boolean;
  help?: string | null;
}

export type Answer = string | number | boolean | null;

/** A question of a record with its answer. */
export interface AnsweredQuestion extends Question {
  answer: Answer;
}

/** What a record stores, e.g. in a `questionnaire` column. */
export interface QuestionnaireSnapshot {
  templateId: string | null;
  templateName: string | null;
  questions: AnsweredQuestion[];
}

/** A template as the API returns it. */
export interface QuestionnaireTemplate {
  id: string;
  /** Key of the questionnaire declaration. */
  questionnaire: string;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  active: boolean;
  questions: Question[];
  createdAt: string;
  updatedAt: string;
  version: number;
}

export const MAX_QUESTIONS = 200;
export const MAX_QUESTION_OPTIONS = 50;
export const QUESTION_LABEL_MAX = 300;
export const QUESTION_HELP_MAX = 1000;
export const ANSWER_TEXT_MAX = 5000;
export const QUESTIONNAIRE_TEMPLATE_NAME_MAX = 120;

const questionId = z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/);
const optionText = z.string().trim().min(1).max(200);

function answerFits(type: QuestionType, value: Answer, options?: readonly string[]): boolean {
  if (value === null) return true;
  switch (type) {
    case 'text':
      return typeof value === 'string' && value.length <= ANSWER_TEXT_MAX;
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'yes-no':
      return typeof value === 'boolean';
    case 'date':
      return typeof value === 'string' && z.iso.date().safeParse(value).success;
    case 'choice':
      return typeof value === 'string' && (options ?? []).includes(value);
  }
}

const questionShape = {
  id: questionId,
  label: z.string().trim().min(1).max(QUESTION_LABEL_MAX),
  type: z.enum(QUESTION_TYPES),
  options: z.array(optionText).max(MAX_QUESTION_OPTIONS).optional(),
  default: z.union([z.string().max(ANSWER_TEXT_MAX), z.number(), z.boolean(), z.null()]).optional(),
  required: z.boolean().optional(),
  help: z.string().trim().max(QUESTION_HELP_MAX).nullable().optional(),
};

function checkQuestion(
  question: { type: QuestionType; options?: string[] },
  context: z.RefinementCtx,
): void {
  if (question.type === 'choice' && !question.options?.length) {
    context.addIssue({ code: 'custom', message: 'A choice needs options', path: ['options'] });
  }
  if (question.type !== 'choice' && question.options !== undefined) {
    context.addIssue({ code: 'custom', message: 'Only a choice has options', path: ['options'] });
  }
}

export const questionSchema = z.strictObject(questionShape).superRefine(checkQuestion);

/** Questions of a template; IDs are unique. Defaults may hold placeholders and are not checked. */
export const questionsSchema = z
  .array(questionSchema)
  .max(MAX_QUESTIONS)
  .refine((questions) => new Set(questions.map((q) => q.id)).size === questions.length, {
    message: 'Question IDs repeat',
  });

export const questionnaireSnapshotSchema = z.strictObject({
  templateId: z.uuid().nullable(),
  templateName: z.string().max(QUESTIONNAIRE_TEMPLATE_NAME_MAX).nullable(),
  questions: z
    .array(
      z
        .strictObject({
          ...questionShape,
          answer: z.union([z.string(), z.number(), z.boolean(), z.null()]),
        })
        .superRefine((question, context) => {
          checkQuestion(question, context);
          if (!answerFits(question.type, question.answer, question.options)) {
            context.addIssue({ code: 'custom', message: 'Answer does not fit', path: ['answer'] });
          }
        }),
    )
    .max(MAX_QUESTIONS)
    .refine((questions) => new Set(questions.map((q) => q.id)).size === questions.length, {
      message: 'Question IDs repeat',
    }),
});

/** A template a module ships as default content (`ModuleDefaults`). */
export interface QuestionnaireTemplateDefault {
  name: string;
  isDefault?: boolean;
  questions: Question[];
}

export const questionnaireTemplateDefaultSchema = z.strictObject({
  name: z.string().trim().min(1).max(QUESTIONNAIRE_TEMPLATE_NAME_MAX),
  isDefault: z.boolean().optional(),
  questions: questionsSchema,
});

const PLACEHOLDER = /\{\{\s*([a-zA-Z][a-zA-Z0-9.]*)\s*\}\}/g;

/**
 * Fills in placeholders like `{{object.address}}` with the given values, for `snapshotFromTemplate`.
 * Placeholders without a value become empty.
 */
export function placeholderResolver(
  values: Readonly<Record<string, string>>,
): (text: string) => string {
  return (text) => text.replace(PLACEHOLDER, (_, key: string) => values[key] ?? '');
}

/**
 * A snapshot of the template's questions with their defaults as answers. `resolve` fills in
 * placeholders of text defaults, e.g. `{{object.name}}`; defaults that do not fit the type stay
 * empty.
 */
export function snapshotFromTemplate(
  template: Pick<QuestionnaireTemplate, 'id' | 'name' | 'questions'>,
  resolve: (text: string) => string = (text) => text,
): QuestionnaireSnapshot {
  return {
    templateId: template.id,
    templateName: template.name,
    questions: template.questions.map((question) => {
      const preset =
        typeof question.default === 'string'
          ? resolve(question.default)
          : (question.default ?? null);
      return {
        ...question,
        answer: answerFits(question.type, preset, question.options) ? preset : null,
      };
    }),
  };
}

/** Whether a question counts as answered: something other than an empty text. */
export function isAnswered(question: Pick<AnsweredQuestion, 'answer'>): boolean {
  const { answer } = question;
  return answer !== null && !(typeof answer === 'string' && answer.trim() === '');
}

/** Answered and all questions, e.g. "12 von 16 beantwortet". */
export function answeredCount(snapshot: QuestionnaireSnapshot | null): {
  answered: number;
  total: number;
} {
  const questions = snapshot?.questions ?? [];
  return { answered: questions.filter(isAnswered).length, total: questions.length };
}
